import { useState } from 'react';
import { Box } from '@mui/material';
import {
  DialogContentText,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
} from '@material-ui/core';
import Button from '@material-ui/core/Button';
import DialogActions from '@material-ui/core/DialogActions';
import DialogContent from '@material-ui/core/DialogContent';
import DialogTitle from '@material-ui/core/DialogTitle';
import {
  LAMPORTS_PER_SOL,
  PublicKey,
  StakeActivationData
} from '@solana/web3.js';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { P, match } from 'ts-pattern';
import strings from '../../../localization';
import StakingService from '../../../services/stakingService';
import { useConnection } from '../../../utils/connection';
import { useSendTransaction } from '../../../utils/notifications';
import { abbreviateAddress, formatLamports, rewardsFormat } from '../../../utils/utils';
import { useBalanceInfo, useWallet } from '../../../utils/wallet';
import StakingSettingsDialog from '../StakingSettingsDialog';
import UnstakeAlertDialog from './UnstakeAlertDialog';

const ONCHAIN_REWARD_PERCENTAGE = 1.15;

export default function AINodeActiveStakeInfo({
  stakeAccount,
  stakeAccountInfo,
  stakingBackendUrl,
  stakingMode,
}) {
  const wallet = useWallet();
  const connection = useConnection();
  const queryClient = useQueryClient();
  const stakeAccountBalance = useBalanceInfo(stakeAccount);
  const [sendTransaction, sending] = useSendTransaction();

  const [showAlert, setShowAlert] = useState(false);

  const stakingService = new StakingService(wallet, connection,
    stakingBackendUrl, stakingMode);

  const { data: stakingPlan } = useQuery({
    queryKey: ['aiNodeStakingPlan', stakingBackendUrl],
    queryFn: async () => {
      return await stakingService.fetchAINodeStakePlan();
    },
    retry: 5,
    retryDelay: 5000,
  });

  const votePubkey = stakingPlan?.validatorVotePubkey ? 
    new PublicKey(stakingPlan.validatorVotePubkey) : null;

  // Get the detailed account info
  const { data: detailedAccountInfo } = useQuery({
    queryKey: ['aiNodeDetailedAccountInfo', stakeAccount.toBase58()],
    queryFn: async () => {
      try {
        const accountInfo = await connection.getParsedAccountInfo(stakeAccount);
        if (!accountInfo.value) return null;
        
        const data = accountInfo.value.data;
        if (data instanceof Buffer) return null;
        
        return {
          parsed: data.parsed,
          owner: accountInfo.value.owner.toBase58()
        };
      } catch (error) {
        console.error('Error fetching detailed account info:', error);
        return null;
      }
    },
    enabled: Boolean(stakeAccount),
  });

  const { data: inflationReward } = useQuery({
    queryKey: ['getInflationReward', stakeAccount.toBase58()],
    queryFn: async () => {
      const result = await connection.getInflationReward([stakeAccount]);
      return result?.[0];
    },
    enabled: Boolean(stakeAccountInfo),
    refetchInterval: 60 * 1000,
  });

  // Get information about AI Node rewards
  const { data: aiNodeRewards } = useQuery({
    queryKey: ['aiNodeRewards', stakeAccount.toBase58()],
    queryFn: async () => {
      try {
        const response = await stakingService.getAINodeRewards(stakeAccount);
        console.log('getAINodeRewards response:', response);
        return response;
      } catch (error) {
        console.error('getAINodeRewards failed:', error);
        throw error;
      }
    },
    enabled: Boolean(stakeAccountInfo),
    refetchInterval: 60 * 1000,
  });

  // Get AI Node certificate information
  const { data: aiNodeCertificate } = useQuery({
    queryKey: ['aiNodeCertificate', wallet.publicKey?.toBase58()],
    queryFn: async () => {
      try {
        return await stakingService.getAINodeCertificate();
      } catch (error) {
        console.error('Failed to get AI Node certificate:', error);
        return null;
      }
    },
    enabled: Boolean(wallet.publicKey),
  });

  const claimPartialStakeReward = async () => {
    try {
      const result = await stakingService.claimAINodeRewards(stakeAccount);
    } catch (error) {
      console.warn(`Failed to claim AI Node rewards: `, error);
    } finally {
      refreshStakeAccountInfo();
    }
  };

  const refreshStakeAccountInfo = () => {
    queryClient.refetchQueries({
      queryKey: ['aiNodeStakeAccountInfo', stakeAccount.toBase58()],
    });
    queryClient.refetchQueries({
      queryKey: ['aiNodeRewards', stakeAccount.toBase58()],
    });
  };

  const handleOnPressUnstake = () => {
    sendTransaction(stakingService.deactivateStake(stakeAccount), {
      onSuccess: refreshStakeAccountInfo,
    });
  };

  const handleOnPressWithdraw = () => {
    sendTransaction(stakingService.withdrawStake(stakeAccount), {
      onSuccess: () => {
        refreshStakeAccountInfo();
      },
    });
  };

  const {
    // Common fields
    id = 0,
    status = 'inactive',
    tierName = '',
  
    // Regular stake fields (M1)
    availableForWithdraw = 0,
    totalExpectedReward = 0,
    initialStakeAmount = 0,
    withdrawnAmount = 0,
    minAllowedWithdrawAmount = 0,
    stakeStartedAt = null,
    stakingPeriodSeconds = 0,
    
    // Locked stake fields (M2/M3)
    initialLockAmount = 0,
    lockStartedAt = null,
    lockPeriodSeconds = 0,
    lockEndDate = null,
    extraRewardPercentage = 0,
  } = aiNodeRewards || {};

  const actualInitialStakeAmount = initialStakeAmount || initialLockAmount || 0;
  const actualStakeStartedAt = stakeStartedAt || lockStartedAt;
  const actualStakingPeriodSeconds = stakingPeriodSeconds || lockPeriodSeconds || 0;

  const totalBalanceExpected = actualInitialStakeAmount > 0 ? 
  Math.floor(actualInitialStakeAmount * ONCHAIN_REWARD_PERCENTAGE) + totalExpectedReward : 0;

  // Determine if claim button should be enabled
  const claimStakeRewardsEnabled = aiNodeRewards && 
    availableForWithdraw >= (minAllowedWithdrawAmount || 1000000);

  // Determine if staking period is unlocked
  let stakingPeriodUnlocked = false;

  // First check using stakeStartedAt + stakingPeriodSeconds
  if (actualStakeStartedAt && actualStakingPeriodSeconds) {
    const startedDate = Date.parse(actualStakeStartedAt);
    if (startedDate) {
      const endedDate = startedDate + actualStakingPeriodSeconds * 1000;
      if (Date.now() >= endedDate) {
        stakingPeriodUnlocked = true;
      }
    }
  }
 
  // Alternatively, check using lockEndDate directly if available
  if (lockEndDate && !stakingPeriodUnlocked) {
    const endDate = Date.parse(lockEndDate);
    if (endDate && Date.now() >= endDate) {
      stakingPeriodUnlocked = true;
    }
  }

  const unstakeEnabled = stakingPeriodUnlocked && !sending;
  
  return (
    <>
      <DialogTitle>
        <Box display="flex" alignItems="center">
          <Box flexGrow={1}>{strings.yourAINodeStake || strings.yourAiNodeStake}</Box>
          <Box>
            <StakingSettingsDialog />
          </Box>
        </Box>
      </DialogTitle>

      <DialogContent>        
        {aiNodeCertificate && (
          <DialogContentText style={{ marginBottom: 16 }}>
            AI Node Type: <strong>{aiNodeCertificate.type || aiNodeCertificate.miner_type}</strong>
          </DialogContentText>
        )}

        <DialogContentText>
          { // @ts-ignore
            match(stakeAccountInfo)
            .with(
              { state: 'activating' },
              () => strings.stakingActivatingDescription
            )
            .otherwise(() => '')
          }
        </DialogContentText>

        <TableContainer component={Paper} variant="outlined">
          <Table>
            <TableBody>
              {[
                [strings.stakeAccount || "Stake Account", abbreviateAddress(stakeAccount)],
                [
                  strings.status || "Status",
                  // @ts-ignore
                  match(stakeAccountInfo.state)
                    .with('activating', () => strings.activating || "Activating")
                    .with('active', () => strings.active || "Active")
                    .with('deactivating', () => strings.deactivating || "Deactivating")
                    .with('inactive', () => strings.inactive || "Inactive")
                    .exhaustive(),
                ],
                [
                  strings.activeStake,
                  `${formatLamports(stakeAccountInfo?.active)} DOMI`,
                ],
                [
                  strings.inactiveStake,
                  `${formatLamports(stakeAccountInfo?.inactive)} DOMI`,
                ],
                [
                  strings.availableForWithdraw,
                  `${rewardsFormat.format(availableForWithdraw / LAMPORTS_PER_SOL)} DOMI`,
                  true,
                ],
                [
                  strings.totalRewardWithdrawn,
                  `${rewardsFormat.format(withdrawnAmount / LAMPORTS_PER_SOL)} DOMI`,
                ],
                [
                  strings.totalBalanceExpected,
                  `${rewardsFormat.format(totalBalanceExpected / LAMPORTS_PER_SOL)} DOMI`,
                ],
              ].map(([name, value, claimButton = false], index) => (
                <TableRow key={index}>
                  <TableCell component="th" scope="row">
                    {name}
                  </TableCell>
                  <TableCell align="right">
                    <>
                      <b>{value}</b>
                      {claimButton ? (
                        <Button color="primary"
                                disabled={!claimStakeRewardsEnabled}
                                onClick={() => claimPartialStakeReward()}
                        >
                          {strings.claim || "Claim"}
                        </Button>
                      ) : null}
                    </>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </DialogContent>

      <DialogActions>
        {match(stakeAccountInfo)
          .with(
            // @ts-ignore
            { state: P.union('activating', 'active') },
            () => (
              <Button
                color="primary"
                onClick={() => setShowAlert(true)}
                disabled={!unstakeEnabled || sending}
              >
                {strings.unstake || "Unstake"}
              </Button>
            )
          )
          .with(
            // @ts-ignore
            { state: P.union('deactivating', 'inactive').select() },
            (state) => (
              <Button
                color="primary"
                onClick={handleOnPressWithdraw}
                disabled={state === 'deactivating' || sending}
              >
                {strings.withdrawStake || "Withdraw Stake"}
              </Button>
            )
          )
          .otherwise(() => null)
        }
      </DialogActions>

      <UnstakeAlertDialog 
        open={showAlert} 
        onConfirm={() => {
          handleOnPressUnstake();
          setShowAlert(false);
        }} 
        onClose={() => setShowAlert(false)} 
      />
    </>
  );
}